import { theme } from 'theme/App.theme';
import makeStyles from '@mui/styles/makeStyles';
import { appPalette } from 'theme/App.palette';

interface IProps {
  isPageDark: boolean;
  buttonColor?: string;
}

const colors = appPalette.colors;

function focusableStyles() {
  return {
    outline: `0.125rem solid`,
    outlineOffset: '0.1875rem',
  };
}

export const useStyles = makeStyles({
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '4rem',
    boxSizing: 'border-box',
    userSelect: 'none',
    textAlign: 'center',
    margin: '0 0 0 0',
    position: 'relative',
    '& *': {
      pointerEvents: 'none',
    },
    '&:focus': {
      outline: 0,
    },
  },
  button_base: {
    border: 0,
    cursor: 'pointer',
    margin: 0,
    display: 'inline-flex',
    outline: 0,
    padding: 0,
    position: 'relative',
    alignItems: 'center',
    userSelect: 'none',
    borderRadius: 0,
    verticalAlign: 'middle',
    justifyContent: 'center',
    textDecoration: 'none',
    backgroundColor: 'transparent',
  },
  focusable: {
    outline: 0,
    position: 'absolute',
    width: '100%',
    height: '100%',
    top: 0,
    left: 0,
    zIndex: 2,
  },
  label: {
    overflow: 'hidden',
    textAlign: 'center',
    textOverflow: 'inherit',
    width: '100%',
    margin: 0,
    position: 'relative',
  },
  text_inherit: {
    ...(theme.themeName === 'honda' && {
      '& *': {
        fontSize: 'inherit',
        fontFamily: 'inherit',
        lineHeight: 'inherit',
        letterSpacing: 'inherit',
      },
    }),
  },
  red: {
    textTransform: 'uppercase',
    padding: '0.125rem',
    color: colors.white,
    transition:
      'fill .3s,color .3s,background-color .3s,background-position .3s',
    background: `linear-gradient(to right,${colors.black} 50%,${colors.acura_red} 0) bottom right`,
    backgroundPositionX: '99%',
    backgroundSize: '205% 100%',
    textDecoration: 'none',
    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        backgroundColor: 'transparent',
        backgroundPosition: '0 bottom',
        textDecoration: 'none',
        color: colors.white,
      },
    },
    '&:focus': {
      '&>.cta-focusable': {
        ...focusableStyles(),
        outlineColor: (props: IProps) =>
          props.isPageDark ? colors.white : colors.black,
      },
    },
  },
  black: {
    textTransform: 'uppercase',
    padding: '0.125rem',
    color: colors.white,
    transition:
      'fill .3s,color .3s,background-color .3s,background-position .3s',
    background: `linear-gradient(to right,${colors.acura_red} 50%,${colors.black} 0) bottom right`,
    backgroundPositionX: '99%',
    backgroundSize: '205% 100%',
    textDecoration: 'none',
    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        backgroundColor: 'transparent',
        backgroundPosition: '0 bottom',
        textDecoration: 'none',
        color: colors.white,
      },
    },
    '&:focus': {
      '&>.cta-focusable': {
        ...focusableStyles(),
        outlineColor: (props: IProps) =>
          props.isPageDark ? colors.white : colors.black,
      },
    },
  },
  redOutlined: {
    textTransform: 'uppercase',
    padding: '0.125rem',
    border: `2px solid ${colors.acura_red}`,
    minHeight: '1.875rem',
    transition:
      'fill .3s,color .3s,background-color .3s,background-position .3s',
    background: `linear-gradient(to right,${colors.acura_red} 50%,transparent 0) bottom right`,
    backgroundPositionX: '99%',
    backgroundSize: '205% 100%',
    textDecoration: 'none',
    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        border: `0.125rem solid ${colors.acura_red}`,
        backgroundColor: 'transparent',
        backgroundPosition: '0 bottom',
        color: colors.white,
        textDecoration: 'none',
      },
    },
    '&:focus': {
      '&>.cta-focusable': {
        ...focusableStyles(),
        outlineColor: (props: IProps) =>
          props.isPageDark ? colors.white : colors.black,
      },
      borderColor: colors.acura_red,
    },
  },
  blackOutlined: {
    textTransform: 'uppercase',
    padding: '0.125rem',
    border: `1px solid ${colors.black}`,
    minHeight: '1.875rem',
    transition:
      'fill .3s,color .3s,background-color .3s,background-position .3s',
    background: `linear-gradient(to right,${colors.black} 50%,transparent 0) bottom right`,
    backgroundPositionX: '99%',
    backgroundSize: '205% 100%',
    textDecoration: 'none',
    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        border: `0.0625rem solid ${colors.black}`,
        backgroundColor: 'transparent',
        backgroundPosition: '0 bottom',
        color: colors.white,
        textDecoration: 'none',
      },
    },
    '&:focus': {
      '&>.cta-focusable': {
        ...focusableStyles(),
        outlineColor: (props: IProps) =>
          props.isPageDark ? colors.white : colors.black,
      },
    },
  },
  blueOutlined: {
    textTransform: 'uppercase',
    padding: '0.125rem',
    border: `2px solid ${colors.blue_dark}`,
    minHeight: '1.875rem',
    transition:
      'fill .3s,color .3s,background-color .3s,background-position .3s',
    background: `linear-gradient(to right,${colors.blue_dark} 50%,transparent 0) bottom right`,
    backgroundPositionX: '99%',
    backgroundSize: '205% 100%',
    textDecoration: 'none',
    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        border: `0.125rem solid ${colors.blue_dark}`,
        backgroundColor: 'transparent',
        backgroundPosition: '0 bottom',
        color: colors.white,
        textDecoration: 'none',
      },
    },
    '&:focus': {
      '&>.cta-focusable': {
        ...focusableStyles(),
        outlineColor: (props: IProps) =>
          props.isPageDark ? colors.white : colors.black,
      },
      borderColor: colors.acura_red,
    },
  },
  filled: (props: IProps) => ({
    textTransform: 'uppercase',
    padding: '0.125rem',
    color: colors.white,
    transition:
      'fill .3s,color .3s,background-color .3s,background-position .3s',
    background: `linear-gradient(to right,${colors.black} 50%,${
      props.buttonColor ? props.buttonColor : colors.acura_red
    } 0) bottom right`,
    backgroundPositionX: '99%',
    backgroundSize: '205% 100%',
    textDecoration: 'none',
    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        backgroundColor: 'transparent',
        backgroundPosition: '0 bottom',
        textDecoration: 'none',
        color: colors.white,
      },
    },
    '&:focus': {
      '&>.cta-focusable': {
        ...focusableStyles(),
        outlineColor: (props: IProps) =>
          props.isPageDark ? colors.white : colors.black,
      },
    },
    ...(theme.themeName === 'honda' && {
      padding: '0.375rem 1rem',
      border: `0.125rem solid ${
        props.buttonColor ? props.buttonColor : colors.honda_red
      }`,
      background: 'none',
      backgroundColor: props.buttonColor ? props.buttonColor : colors.honda_red,
      transition: 'fill .3s,color .3s,background-color .3s',
      '@media (hover: hover) and (pointer: fine)': {
        '&:hover': {
          backgroundColor: 'transparent',
          color: props.buttonColor ? props.buttonColor : colors.honda_red,
        },
      },
    }),
  }),
  text: {
    textTransform: 'uppercase',
    backgroundImage: `linear-gradient(transparent,transparent),linear-gradient(${colors.acura_red},${colors.acura_red})`,
    backgroundPosition: '100% 100%,0 100%',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '100% 0.125rem,0 0.125rem',
    padding: '0.125rem ',
    color: 'inherit',
    transition: 'background-size .4s linear',
    textDecoration: 'none',
    '&:hover': {
      backgroundSize: '0 0.125rem,100% 0.125rem',
    },
    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        backgroundSize: '0 0.125rem,100% 0.125rem',
      },
    },
    '&:focus': {
      '&>.cta-focusable': {
        ...focusableStyles(),
        outlineColor: (props: IProps) =>
          props.isPageDark ? colors.white : colors.black,
      },
    },
    '&:disabled': {
      backgroundSize: '100% 0.125rem, 0 0.125rem',
      opacity: 0.5,
    },
    ...(theme.themeName === 'honda' && {
      backgroundImage: `linear-gradient(transparent,transparent),linear-gradient(${colors.honda_blue},${colors.honda_blue})`,
      textTransform: 'none',
      '&:hover': {
        backgroundSize: '100% 0.125rem,0 0.125rem',
      },
      '@media (hover: hover) and (pointer: fine)': {
        '&:hover': {
          backgroundSize: '100% 0.125rem,0 0.125rem',
        },
      },
    }),
  },
  roundedButton: {
    borderRadius: `1.4375rem`,
  },
  multiLineTextRed: {
    padding: '0.125rem ',
    color: colors.acura_red,
    textDecoration: 'none',
    position: 'relative',
    '& .cta-label': {
      position: 'static',
      '& > span': {
        display: 'inline',
        position: 'static',
        backgroundImage: `linear-gradient(transparent,transparent),linear-gradient(${colors.acura_red},${colors.acura_red})`,
        backgroundPosition: '100% 100%,0 100%',
        backgroundRepeat: 'no-repeat',
        backgroundSize: '100% 0.125rem,0 0.125rem',
        transition: 'background-size .4s linear',
        paddingBottom: '0.125rem',
        '& sup, button': {
          position: 'relative',
          zIndex: 4,
        },
      },
    },
    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        color: colors.acura_red,
        '&>.cta-label span': {
          backgroundSize: '0 0.125rem,100% 0.125rem',
        },
      },
    },
    '&:focus': {
      '&>.cta-focusable': {
        ...focusableStyles(),
        outlineColor: (props: IProps) =>
          props.isPageDark ? colors.white : colors.black,
      },
    },
  },
  containedText: {
    textTransform: 'uppercase',
    padding: '0.125rem ',
    color: 'inherit',
    textDecoration: 'none',
    '&>.cta-label': {
      width: 'auto !important',
      paddingBottom: '0.125rem',
      backgroundImage: `linear-gradient(transparent,transparent),linear-gradient(${colors.acura_red},${colors.acura_red})`,
      backgroundPosition: '100% 100%,0 100%',
      backgroundRepeat: 'no-repeat',
      backgroundSize: '100% 0.125rem,0 0.125rem',
      transition: 'background-size .4s linear',
      color: colors.white,
    },
    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        '&>.cta-label': {
          backgroundSize: '0 0.125rem,100% 0.125rem',
        },
      },
    },
    '&:focus': {
      '&>.cta-focusable': {
        outline: `0.125rem solid`,
        outlineOffset: '-0.125rem',
        outlineColor: (props: IProps) =>
          props.isPageDark ? colors.white : colors.black,
      },
    },
  },
  containedTextRed: {
    backgroundColor: colors.acura_red,
    textTransform: 'uppercase',
    padding: '0.125rem ',
    color: colors.white,
    textDecoration: 'none',
    '&>.cta-label': {
      width: 'auto !important',
      paddingBottom: '0.125rem',
      backgroundImage: `linear-gradient(transparent,transparent),linear-gradient(${colors.white},${colors.white})`,
      backgroundPosition: '100% 100%,0 100%',
      backgroundRepeat: 'no-repeat',
      backgroundSize: '100% 0.125rem,0 0.125rem',
      transition: 'background-size .4s linear',
      color: colors.white,
    },
    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        '&>.cta-label': {
          backgroundSize: '0 0.125rem,100% 0.125rem',
        },
      },
    },
    '&:focus': {
      '&>.cta-focusable': {
        outline: `0.125rem solid`,
        outlineOffset: '-0.125rem',
        outlineColor: (props: IProps) =>
          props.isPageDark ? colors.white : colors.black,
      },
    },
    '&:disabled': {
      backgroundColor: colors.light_grey_4,
    },
  },
  underlinedText: {
    padding: '0.125rem ',
    textTransform: 'unset',
    textDecoration: 'underline',
    textUnderlineOffset: '1px',
    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        color: colors.acura_red,
      },
    },
    '&:focus-visible': {
      textDecoration: 'underline',
      '&>.cta-focusable': {
        ...focusableStyles(),
        outlineColor: (props: IProps) =>
          props.isPageDark ? colors.white : colors.black,
      },
    },
  },
  underlinedTextRed: {
    padding: '0.125rem ',
    textTransform: 'unset',
    textDecoration: 'underline',
    textUnderlineOffset: '1px',
    color: colors.acura_red,
    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        color: colors.black,
        textDecoration: 'none',
      },
    },
    '&:focus-visible': {
      '&>.cta-focusable': {
        ...focusableStyles(),
        outlineColor: (props: IProps) =>
          props.isPageDark ? colors.white : colors.black,
      },
    },
  },
  simple: {
    border: `none`,
    padding: '0',
    font: 'inherit',
    '&:focus': {
      'body:not(.acr-focus-disabled) &': {
        textDecoration: 'none',
        ...focusableStyles(),
        outlineColor: (props: IProps) =>
          props.isPageDark ? colors.white : colors.black,
        outlineOffset: '0.0625rem',
      },
    },
  },
});
